// API client.
//
// Relative paths only (architecture.md section 15.4). Firebase Hosting rewrites /api/** to the
// Cloud Run service in the same project, so a hostname is never needed and must never be baked
// into the bundle - that is what lets Production promote the UAT bundle unchanged.

import { currentUserToken } from './auth/session.js';

// The fetch every call shares: the token, the method, the body, and the refusal turned into an
// Error the screen can show. Returns the Response for the caller to read as JSON or as bytes.
async function send(path, { method = 'GET', body } = {}) {
  // Null locally, where the backend assumes an identity and there is no token to send.
  const token = await currentUserToken();

  const response = await fetch(path, {
    method,
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(body ? { 'Content-Type': 'application/json' } : {}),
    },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });

  if (!response.ok) {
    let detail = `${response.status} ${response.statusText}`;
    let fields = null;
    let body = null;
    try {
      const problem = await response.json();
      if (Array.isArray(problem.detail)) {
        // FastAPI's validation shape, which the API also uses for reference-list errors. Keyed by
        // business field so a form can render each message against its own input rather than as
        // one banner that does not say which field is wrong.
        fields = Object.fromEntries(
          problem.detail.map((item) => [item.loc[item.loc.length - 1], item.msg]),
        );
        detail = 'Some fields need attention';
      } else if (problem.detail && typeof problem.detail === 'object') {
        // A structured problem: the counterparty endpoints answer 409 with the record or the
        // candidates that caused it, because the caller's next move is to offer them. Kept whole
        // on the error rather than flattened to a string - a message is all a banner needs, and
        // this needs more than a banner.
        body = problem.detail;
        detail = problem.detail.message ?? detail;
      } else if (problem.detail) {
        detail = problem.detail;
      }
    } catch {
      // Non-JSON error body; the status line is all we have.
    }
    const error = new Error(detail);
    error.status = response.status;
    error.fields = fields;
    error.detail = body;
    throw error;
  }
  return response;
}

async function request(path, options) {
  const response = await send(path, options);
  // A delete answers 204 and carries nothing to parse.
  if (response.status === 204) return null;
  return response.json();
}

// A file the API builds or streams, saved through the browser under `filename`. Fetched rather
// than linked: a plain <a href> carries no bearer token, so it works locally through the bypass
// and is refused deployed. The object URL is revoked once the click has been dispatched.
export async function downloadFile(path, filename) {
  const response = await send(path);
  const url = URL.createObjectURL(await response.blob());
  try {
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    anchor.click();
  } finally {
    URL.revokeObjectURL(url);
  }
}

export const getMe = () => request('/api/me');

// The register's query string is built by src/deals/registerUrl.js (via src/ui/tableUrl.js) and passed through unchanged,
// so the URL the user sees and the URL we request cannot disagree.
export const listDeals = (search) => request(`/api/deals${search ? `?${search}` : ''}`);

export const getDeal = (preDealId) => request(`/api/deals/${encodeURIComponent(preDealId)}`);

export const createDeal = (fields) => request('/api/deals', { method: 'POST', body: fields });

// `version` is the value read with the deal. The server refuses a stale one with 409 rather than
// overwriting a concurrent edit.
export const updateDeal = (preDealId, fields, version) => request(
  `/api/deals/${encodeURIComponent(preDealId)}`,
  { method: 'PUT', body: { ...fields, version } },
);

// The deal flow (TX-US-0043). A move is a deal writer's; a decision an approver's. Each carries
// the version read with the deal and is refused with 409 when stale.
export const getDealFlow = (preDealId) => request(
  `/api/deals/${encodeURIComponent(preDealId)}/flow`,
);

export const moveDeal = (preDealId, move, version, reason = null) => request(
  `/api/deals/${encodeURIComponent(preDealId)}/moves`,
  { method: 'POST', body: { move, version, reason } },
);

export const decideDeal = (preDealId, step, decision, version, reason = null) => request(
  `/api/deals/${encodeURIComponent(preDealId)}/decisions`,
  { method: 'POST', body: { step, decision, version, reason } },
);

// The Incoterms with what each says, and the document types. Not under /api/deals, where it
// would be taken for a deal's identifier.
export const getDealVocabulary = () => request('/api/deal-vocabulary');

// ADMIN's alone (ADR-0012). The deal is marked deleted and answers 404 from then on.
export const deleteDeal = (preDealId) => request(
  `/api/deals/${encodeURIComponent(preDealId)}`,
  { method: 'DELETE' },
);

export const getReferenceLists = () => request('/api/reference');

export const addReferenceEntry = (listName, code, label) => request(
  `/api/reference/${encodeURIComponent(listName)}`,
  { method: 'POST', body: { code, label } },
);

export const listLocations = () => request('/api/locations');

export const addLocation = (location) => request('/api/locations', {
  method: 'POST',
  body: location,
});

// The picker's list, narrowed to a category when the field has one (TX-US-0016 AC7); the API
// keeps the uncategorised in every list.
export const listCounterparties = (category) =>
  request(`/api/counterparties${category ? `?category=${encodeURIComponent(category)}` : ''}`);

// The register (TX-US-0016): a filtered, sorted page; one record; an edit carrying its version.
export const pageCounterparties = (search) => request(`/api/counterparties/page?${search}`);
export const getCounterparty = (id) => request(`/api/counterparties/${encodeURIComponent(id)}`);
export const updateCounterparty = (id, body) => request(
  `/api/counterparties/${encodeURIComponent(id)}`,
  { method: 'PUT', body },
);
// A step forward waits on the gate's sheet: a 409 of `kind: kyc_incomplete` or
// `assessment_incomplete` names what is missing. A rejection carries its reason (TX-US-0036).
// `more` carries an override's answer (TX-US-0042): the approver and the evidence of a grant, or
// whether a rejection was a decline or a withdrawal.
export const transitionCounterparty = (id, target, version, reason, more = {}) => request(
  `/api/counterparties/${encodeURIComponent(id)}/transition`,
  { method: 'POST', body: { target, version, ...(reason ? { reason } : {}), ...more } },
);

// The legal review Legal keeps (TX-US-0042), saved as the other sheets are.
const legalReviewPath = (id) => `/api/counterparties/${encodeURIComponent(id)}/legal-review`;

export const getLegalReview = (id) => request(legalReviewPath(id));

export const saveLegalReview = (id, sheet, version) => request(
  legalReviewPath(id),
  { method: 'PUT', body: { sheet, version } },
);

// The KYC review Compliance prepares before approving KYC (TX-US-0035), saved as the credit
// assessment is: against the version read, 0 when nobody has saved one.
const kycReviewPath = (id) => `/api/counterparties/${encodeURIComponent(id)}/kyc-review`;

export const getKycReview = (id) => request(kycReviewPath(id));

export const saveKycReview = (id, sheet, version) => request(
  kycReviewPath(id),
  { method: 'PUT', body: { sheet, version } },
);

// The credit assessment Risk prepares before approving (TX-US-0027). `version` is the one read
// with the assessment - 0 when nobody has saved one - and a stale one is refused with 409. A 409
// carrying `detail.missing` names the approval core an approved counterparty must keep.
const creditAssessmentPath = (id) =>
  `/api/counterparties/${encodeURIComponent(id)}/credit-assessment`;

export const getCreditAssessment = (id) => request(creditAssessmentPath(id));

export const saveCreditAssessment = (id, sheet, version) => request(
  creditAssessmentPath(id),
  { method: 'PUT', body: { sheet, version } },
);

// A counterparty's documents (TX-US-0033). The upload itself goes to storage on the contract the
// request returns; a delete takes the document off the list and the API keeps it.
const counterpartyDocumentsPath = (id) =>
  `/api/counterparties/${encodeURIComponent(id)}/documents`;

const counterpartyDocumentPath = (id, documentId) =>
  `${counterpartyDocumentsPath(id)}/${encodeURIComponent(documentId)}`;

export const listCounterpartyDocuments = (id) => request(counterpartyDocumentsPath(id));

// `contentType` is the caller's to say: a browser on a machine without Office reports none for
// a workbook, and the policy names the file by its extension then (documents/dealFiles.js).
export const requestCounterpartyDocumentUpload = (id, file, contentType = file.type) => request(
  counterpartyDocumentsPath(id),
  {
    method: 'POST',
    body: { filename: file.name, content_type: contentType, size_bytes: file.size },
  },
);

export const confirmCounterpartyDocumentUpload = (id, documentId) => request(
  `${counterpartyDocumentPath(id, documentId)}/confirm`,
  { method: 'POST' },
);

// `inline` is the preview: the browser shows the file where the page puts it. Without it the
// URL downloads the file under its own name.
export const getCounterpartyDocumentDownload = (id, documentId, { inline = false } = {}) =>
  request(`${counterpartyDocumentPath(id, documentId)}/download${inline ? '?inline=true' : ''}`);

export const deleteCounterpartyDocument = (id, documentId) => request(
  counterpartyDocumentPath(id, documentId),
  { method: 'DELETE' },
);

// A counterparty's comments (TX-US-0037): a page of twenty, newest first, and the next by the
// cursor the page before it gave.
const counterpartyCommentsPath = (id) => `/api/counterparties/${encodeURIComponent(id)}/comments`;

export const pageCounterpartyComments = (id, cursor) => request(
  `${counterpartyCommentsPath(id)}${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ''}`,
);

export const addCounterpartyComment = (id, body) => request(
  counterpartyCommentsPath(id),
  { method: 'POST', body: { body } },
);

// A counterparty's quotes and ratings (TX-US-0040, ADR-0013).
const counterpartyQuotesPath = (id) => `/api/counterparties/${encodeURIComponent(id)}/quotes`;
const counterpartyRatingsPath = (id) => `/api/counterparties/${encodeURIComponent(id)}/ratings`;
const withCursor = (path, cursor) => `${path}${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ''}`;

export const pageCounterpartyQuotes = (id, cursor) => request(
  withCursor(counterpartyQuotesPath(id), cursor),
);

export const addCounterpartyQuote = (id, body) => request(
  counterpartyQuotesPath(id),
  { method: 'POST', body },
);

// Only the email that added the quote deletes it; anyone else is answered 403.
export const deleteCounterpartyQuote = (id, quoteId) => request(
  `${counterpartyQuotesPath(id)}/${encodeURIComponent(quoteId)}`,
  { method: 'DELETE' },
);

export const pageCounterpartyRatings = (id, cursor) => request(
  withCursor(counterpartyRatingsPath(id), cursor),
);

// The caller's own rating, or { rating: null }.
export const getMyCounterpartyRating = (id) => request(`${counterpartyRatingsPath(id)}/mine`);

// Gives the caller's rating or replaces it; answers the rating and the counterparty's new average.
export const rateCounterparty = (id, stars, comment) => request(
  counterpartyRatingsPath(id),
  { method: 'PUT', body: { stars, comment } },
);

// Case-insensitive and mid-string, matched against the normalised name - so "vitol s.a." finds
// "Vitol SA" without the BD guessing how the record was punctuated. Fewer than two characters
// matches most of the register, and the server answers with nothing rather than all of it.
export const searchCounterparties = (q, category) => request(
  `/api/counterparties?q=${encodeURIComponent(q)}${category ? `&category=${encodeURIComponent(category)}` : ''}`,
);

// A 409 here is not always a refusal. `detail.kind` says which it is: `duplicate_name` is the
// index and is certain, `likely_duplicate` is the advisory gate and carries candidates the BD
// resolves - either by using one of them or by reposting with confirmedDistinct.
export const createCounterparty = (counterparty) => request('/api/counterparties', {
  method: 'POST',
  body: counterparty,
});

const documentsPath = (preDealId) => `/api/deals/${encodeURIComponent(preDealId)}/documents`;

export const listDocuments = (preDealId) => request(documentsPath(preDealId));

// `contentType` is the caller's to say: a browser on a machine without Office reports none for
// a workbook, and the policy names the file by its extension then (documents/dealFiles.js). The
// type is what the document is (TX-US-0043), chosen when it was dropped; the API requires one.
export const requestDocumentUpload = (
  preDealId, file, contentType = file.type, documentType,
) => request(
  documentsPath(preDealId),
  {
    method: 'POST',
    body: {
      filename: file.name,
      content_type: contentType,
      size_bytes: file.size,
      document_type: documentType,
    },
  },
);

export const confirmDocumentUpload = (preDealId, documentId) => request(
  `${documentsPath(preDealId)}/${encodeURIComponent(documentId)}/confirm`,
  { method: 'POST' },
);

export const getDocumentDownload = (preDealId, documentId) => request(
  `${documentsPath(preDealId)}/${encodeURIComponent(documentId)}/download`,
);

// Refused with 403 for anyone but the document's uploader, as a delete is (TX-US-0043).
export const retypeDocument = (preDealId, documentId, documentType) => request(
  `${documentsPath(preDealId)}/${encodeURIComponent(documentId)}`,
  { method: 'PATCH', body: { document_type: documentType } },
);

// Refused with 403 for anyone but the document's uploader (ADR-0012).
export const deleteDocument = (preDealId, documentId) => request(
  `${documentsPath(preDealId)}/${encodeURIComponent(documentId)}`,
  { method: 'DELETE' },
);

// The delivery tracker (TX-US-0010). The column definitions are served, not spelled here: the
// tracker renders whatever the leg carries.
export const getDeliveryColumns = () => request('/api/deliveries/columns');

const deliveriesPath = (leg) => `/api/deliveries/${encodeURIComponent(leg)}`;

const deliveryPath = (leg, deliveryId) =>
  `${deliveriesPath(leg)}/${encodeURIComponent(deliveryId)}`;

export const listDeliveries = (leg, search) => request(
  `${deliveriesPath(leg)}${search ? `?${search}` : ''}`,
);

export const getDelivery = (leg, deliveryId) => request(deliveryPath(leg, deliveryId));

// A column's filter offers its values among the rows the other filters admit (TX-US-0020), and
// the total row is the count and sums over every row the filters admit - not the page on
// screen. `search` is the `f.*` query string from tableUrl's filterSearch.
export const getDeliveryFacet = (leg, key, search) => request(
  `${deliveriesPath(leg)}/facets/${encodeURIComponent(key)}${search ? `?${search}` : ''}`,
);

export const getDeliveryTotals = (leg, search) => request(
  `${deliveriesPath(leg)}/totals${search ? `?${search}` : ''}`,
);

export const createDelivery = (leg, values) => request(deliveriesPath(leg), {
  method: 'POST',
  body: { values },
});

// `version` is the value read with the row; a stale one is refused with 409, as for deals.
export const updateDelivery = (leg, deliveryId, values, version) => request(
  deliveryPath(leg, deliveryId),
  { method: 'PUT', body: { values, version } },
);

export const deleteDelivery = (leg, deliveryId) => request(deliveryPath(leg, deliveryId), {
  method: 'DELETE',
});

// Attachments on a delivery row (TX-US-0011). The upload itself goes to storage on the contract
// the API returns - see documents/files.js putDocument - and is confirmed here afterwards.
const attachmentsPath = (leg, deliveryId) => `${deliveryPath(leg, deliveryId)}/attachments`;

const attachmentPath = (leg, deliveryId, attachmentId) =>
  `${attachmentsPath(leg, deliveryId)}/${encodeURIComponent(attachmentId)}`;

export const listAttachments = (leg, deliveryId) => request(attachmentsPath(leg, deliveryId));

export const requestAttachmentUpload = (leg, deliveryId, file) => request(
  attachmentsPath(leg, deliveryId),
  {
    method: 'POST',
    body: { filename: file.name, content_type: file.type, size_bytes: file.size },
  },
);

export const confirmAttachmentUpload = (leg, deliveryId, attachmentId) => request(
  `${attachmentPath(leg, deliveryId, attachmentId)}/confirm`,
  { method: 'POST' },
);

// `inline` asks for a URL the browser may render in place - an image, a PDF in its own viewer -
// rather than one it saves.
export const getAttachmentDownload = (leg, deliveryId, attachmentId, { inline = false } = {}) =>
  request(`${attachmentPath(leg, deliveryId, attachmentId)}/download${inline ? '?inline=true' : ''}`);

export const deleteAttachment = (leg, deliveryId, attachmentId) => request(
  attachmentPath(leg, deliveryId, attachmentId),
  { method: 'DELETE' },
);

export const downloadAttachmentsZip = (leg, deliveryId) =>
  downloadFile(`${attachmentsPath(leg, deliveryId)}/zip`, `${deliveryId}-attachments.zip`);

// The whole tracker as one workbook (TX-US-0015); the API names the file for the day.
export const downloadTrackerExport = (filename) => downloadFile('/api/deliveries/export', filename);

// User management (TX-US-0021). Admin-only; the API rejects non-admin callers with 403.
export const listUsers = () => request('/api/users');

export const createUser = (user) => request('/api/users', { method: 'POST', body: user });

export const updateUser = (email, user) => request(
  `/api/users/${encodeURIComponent(email)}`,
  { method: 'PUT', body: user },
);

export const deleteUser = (email) => request(
  `/api/users/${encodeURIComponent(email)}`,
  { method: 'DELETE' },
);

export const assumeRole = (role) => request('/api/me/assume-role', {
  method: 'POST',
  body: { role },
});

export const clearAssumedRole = () => request('/api/me/assume-role', { method: 'DELETE' });

// Notifications (TX-US-0023).
export const listNotifications = (includeResolved = false) =>
  request(`/api/notifications${includeResolved ? '?include_resolved=true' : ''}`);

export const getUnreadCount = () => request('/api/notifications/unread-count');

export const markNotificationRead = (notificationId) => request(
  `/api/notifications/${encodeURIComponent(notificationId)}/read`,
  { method: 'POST' },
);

export const markAllNotificationsRead = () => request(
  '/api/notifications/read-all',
  { method: 'POST' },
);

// Economics (TX-US-0025). Calculation writes nothing: the scenario screen calls it on every slider
// movement. Amounts cross the wire as strings in both directions, so nothing is rounded by a float.
const economicModelPath = (modelId) => `/api/economics/models/${encodeURIComponent(modelId)}`;

export const listEconomicModels = () => request('/api/economics/models');

export const getEconomicModel = (modelId) => request(economicModelPath(modelId));

export const calculateCashflow = (modelId, values) => request(
  `${economicModelPath(modelId)}/calculate`,
  { method: 'POST', body: values },
);

export const listScenarios = (modelId) => request(`${economicModelPath(modelId)}/scenarios`);

export const getScenario = (modelId, scenarioId) => request(
  `${economicModelPath(modelId)}/scenarios/${encodeURIComponent(scenarioId)}`,
);

export const saveScenario = (modelId, name, values) => request(
  `${economicModelPath(modelId)}/scenarios`,
  { method: 'POST', body: { name, values } },
);

export const deleteScenario = (modelId, scenarioId) => request(
  `${economicModelPath(modelId)}/scenarios/${encodeURIComponent(scenarioId)}`,
  { method: 'DELETE' },
);

export const saveOverScenario = (modelId, scenarioId, values) => request(
  `${economicModelPath(modelId)}/scenarios/${encodeURIComponent(scenarioId)}`,
  { method: 'PUT', body: { values } },
);

// The fleet requirement calculator (TX-US-0030): one shared list of routes. Every write answers
// with the whole list and its totals, recalculated.
const FLEET_ROUTES = '/api/economics/fleet/routes';

export const listFleetRoutes = () => request(FLEET_ROUTES);

export const addFleetRoute = (fields) => request(FLEET_ROUTES, { method: 'POST', body: fields });

export const saveFleetRoute = (routeId, fields) => request(
  `${FLEET_ROUTES}/${encodeURIComponent(routeId)}`,
  { method: 'PUT', body: fields },
);

export const deleteFleetRoute = (routeId) => request(
  `${FLEET_ROUTES}/${encodeURIComponent(routeId)}`,
  { method: 'DELETE' },
);

// The commercial hub (TX-US-0028): a BD's own activity updates. The owner is the caller; no call
// names a person. A session's writes answer with the session, so the screen holds one object.
const hubSessionPath = (sessionId) => `/api/hub/sessions/${encodeURIComponent(sessionId)}`;

export const getHubToday = (localDate) => request(
  `/api/hub/today?local_date=${encodeURIComponent(localDate)}`,
);

export const listHubSessions = (cursor) => request(
  `/api/hub/sessions?limit=10${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ''}`,
);

export const getHubSession = (sessionId) => request(hubSessionPath(sessionId));

export const openHubSession = (localDate, timezone) => request('/api/hub/sessions', {
  method: 'POST',
  body: { local_date: localDate, timezone },
});

export const saveHubText = (sessionId, text) => request(`${hubSessionPath(sessionId)}/text`, {
  method: 'PUT',
  body: { text },
});

export const submitHubSession = (sessionId) => request(`${hubSessionPath(sessionId)}/submit`, {
  method: 'POST',
});

export const discardHubSession = (sessionId) => request(hubSessionPath(sessionId), {
  method: 'DELETE',
});

export const requestHubRecordingUpload = (sessionId, take) => request(
  `${hubSessionPath(sessionId)}/recording`,
  {
    method: 'POST',
    body: {
      content_type: take.contentType,
      size_bytes: take.blob.size,
      duration_seconds: take.durationSeconds,
    },
  },
);

export const confirmHubRecordingUpload = (sessionId) => request(
  `${hubSessionPath(sessionId)}/recording/confirm`,
  { method: 'POST' },
);

export const getHubRecordingDownload = (sessionId) => request(
  `${hubSessionPath(sessionId)}/recording/download`,
);

export const deleteHubRecording = (sessionId) => request(
  `${hubSessionPath(sessionId)}/recording`,
  { method: 'DELETE' },
);

export const requestHubAttachmentUpload = (sessionId, file) => request(
  `${hubSessionPath(sessionId)}/attachments`,
  {
    method: 'POST',
    body: { filename: file.name, content_type: file.type || '', size_bytes: file.size },
  },
);

const hubAttachmentPath = (sessionId, attachmentId) =>
  `${hubSessionPath(sessionId)}/attachments/${encodeURIComponent(attachmentId)}`;

export const confirmHubAttachmentUpload = (sessionId, attachmentId) => request(
  `${hubAttachmentPath(sessionId, attachmentId)}/confirm`,
  { method: 'POST' },
);

export const getHubAttachmentDownload = (sessionId, attachmentId) => request(
  `${hubAttachmentPath(sessionId, attachmentId)}/download`,
);

export const deleteHubAttachment = (sessionId, attachmentId) => request(
  hubAttachmentPath(sessionId, attachmentId),
  { method: 'DELETE' },
);
